// The commerce family's `webhook_source.health_changed` (#6167) — split out of
// `commerce.ts` purely for size (biome's 800-line file cap), the `review-knowledge.ts`
// posture: this group is spread into `TERMINAL_RENDERERS_COMMERCE`, so the family's
// partition and coverage tests see it where the vocabulary (`../../audit/kind-commerce.ts`,
// beside the other `webhook_source.*` kinds) puts it.
//
// The output is parsed WHOLE against its writer's schema, the `forge.mr.*` posture: a
// line naming a transition without both of its ends would be worse than no line, and a
// withheld or malformed row renders nothing. Never `'omit'`: an omitted kind is dropped
// from `TERMINAL_KINDS`, and a source going unhealthy is the line a maintainer needs.
// One level for the kind (per kind, never per row, `wire/terminal.ts`): `warn`, because
// the transitions a maintainer must act on are the failures; the sentence says which way
// it moved. The subject is the row's session, as the other `webhook_source.*` verbs render.

import type { TerminalRenderContext, TerminalRendered } from '../../../wire/terminal';
import { terminalRendererGroup } from '../../../wire/terminal';
import { WebhookSourceHealthChangedOutputSchema } from '../../audit/payload';

/**
 * One `webhook_source.health_changed` row as one of four lines, chosen by where the
 * source went: `cleared` (connected, no error: names what it cleared), `failure_recorded`
 * (still connected, with an error: ingest's first strike) or `errored`. `null` when the
 * output does not parse.
 *
 * The fourth, `cleared_unrecognized` (#6170), is a clear whose `from` is `unrecognized`:
 * a stored reason outside the closed sets has no name worth printing. A `to` of
 * `unrecognized` prints as the reason; neither writer produces one, since `to` is the
 * closed value it just wrote.
 */
function renderWebhookSourceHealthChanged(ctx: TerminalRenderContext): TerminalRendered | null {
  const parsed = WebhookSourceHealthChangedOutputSchema.safeParse(ctx.row.output);
  if (!parsed.success) return null;
  const { sourceId, from, to } = parsed.data;
  const cleared = to.lastError === null && to.status === 'connected';
  const line =
    cleared && from.lastError === 'unrecognized'
      ? {
          key: 'terminal.webhook_source.health_changed.cleared_unrecognized' as const,
          params: { sourceId },
        }
      : cleared
        ? {
            key: 'terminal.webhook_source.health_changed.cleared' as const,
            params: { sourceId, from: from.lastError ?? from.status },
          }
        : {
            key:
              to.status === 'connected'
                ? ('terminal.webhook_source.health_changed.failure_recorded' as const)
                : ('terminal.webhook_source.health_changed.errored' as const),
            params: { sourceId, reason: to.lastError ?? to.status },
          };
  const { sessionId } = ctx.row;
  return {
    line,
    subject: { kind: 'session', id: sessionId, label: sessionId },
    refs: { sessionId },
  };
}

export const TERMINAL_RENDERERS_WEBHOOK_SOURCE_HEALTH = terminalRendererGroup({
  'webhook_source.health_changed': {
    level: 'warn',
    actor: 'system',
    render: renderWebhookSourceHealthChanged,
  },
});
